import { createSelector, createSlice } from '@reduxjs/toolkit';
import { DetailArticlePosition, UploadStockInvoiceStepIndex, UploadSurplusItemsStepIndex, } from '../models';
import { isEveryFieldDefined } from '@/core/utils/objects';
// TODO: remove all unused props and methods during stockReceipt refactoring.
const userEmptyInvoiceSettings = {
    invoiceIdCell: undefined,
    dateCell: undefined,
    articleColumn: undefined,
    startRow: undefined,
    qtyColumn: undefined,
    priceColumn: undefined,
    sellingPriceColumn: undefined,
    brandColumn: undefined,
    nameColumn: undefined,
    countryCodeColumn: undefined,
    gtdColumn: undefined,
    // trackingCodeColumn: undefined,
    // trackingQtyColumn: undefined,
    // trackingSymbolColumn: undefined,
};
const userEmptySurplusItemsSettings = {
    brandColumn: undefined,
    articleColumn: undefined,
    quantityColumn: undefined,
    startRow: undefined,
};
const initialState = {
    inboxDateRangeFilter: {
        // Should always be `null`, otherwise it fires additional request with this initial values right before the
        // necessary request with values gotten from local storage.
        dateFrom: null,
        dateTo: null,
    },
    inboxArticleSearchTerm: '',
    inboxItemCount: undefined,
    inboxIsReceipted: false,
    verificationItemCount: undefined,
    ownerLocation: 'invoice',
    // placeStockReceiptItemDialog: {
    // 	open: false,
    // 	data: undefined,
    // },
    uploadInvoiceDialog: {
        open: false,
        activeStep: UploadStockInvoiceStepIndex.StockSelect,
        invoice: undefined,
        isInvoiceUploading: false,
        invoiceUploadErrorMessage: undefined,
        // Handles the case if no settings returned (undefined or null), that is how it should be in new backend, probably.
        // invoiceSettings: {
        // 	// invoiceIdCell: {
        // 	// 	column: undefined,
        // 	// 	row: undefined,
        // 	// },
        // 	// dateCell: {
        // 	// 	column: undefined,
        // 	// 	row: undefined,
        // 	// },
        // 	invoiceIdCell: undefined,
        // 	dateCell: undefined,
        // 	// articleColumn: {
        // 	// 	position: DetailArticlePosition.WholeString,
        // 	// 	column: undefined,
        // 	// },
        // 	articleColumn: undefined,
        // 	startRow: undefined,
        // 	qtyColumn: undefined,
        // 	priceColumn: undefined,
        // 	brandColumn: undefined,
        // 	nameColumn: undefined,
        // 	countryCodeColumn: undefined,
        // 	gtdColumn: undefined,
        // 	trackingCodeColumn: undefined,
        // 	trackingQtyColumn: undefined,
        // 	trackingSymbolColumn: undefined,
        // },
        invoiceSettings: userEmptyInvoiceSettings,
        activeSettingKey: 'invoiceIdCell',
    },
    uploadSurplusItemsDialog: {
        open: false,
        activeStep: UploadSurplusItemsStepIndex.StockSelect,
        surplusItemsPreview: undefined,
        isRequestFetching: false,
        itemsUploadErrorMessage: undefined,
        surplusItemsSettings: userEmptySurplusItemsSettings,
        activeSettingKey: 'brandColumn',
        isEverySettingDefined: false,
    },
};
const slice = createSlice({
    name: 'stockReceipt',
    initialState,
    reducers: {
        setInboxDateRangeFilter: (state, action) => {
            state.inboxDateRangeFilter = action.payload;
        },
        setInboxItemCount: (state, action) => {
            state.inboxItemCount = action.payload;
        },
        setVerificationItemCount: (state, action) => {
            state.verificationItemCount = action.payload;
        },
        setInboxArticleSearchTerm: (state, action) => {
            state.inboxArticleSearchTerm = action.payload;
        },
        setInboxIsReceipted: (state, action) => {
            state.inboxIsReceipted = action.payload;
        },
        setOwnerLocation: (state, action) => {
            state.ownerLocation = action.payload;
        },
        // openPlaceReceiptItemDialog: (state, action: PayloadAction<PlaceReceiptItemDialogData>) => {
        // 	state.placeStockReceiptItemDialog = {
        // 		...state.placeStockReceiptItemDialog,
        // 		open: true,
        // 		// data: {
        // 		// 	...action.payload,
        // 		// },
        // 		data: action.payload,
        // 	};
        // },
        // closePlaceReceiptItemDialog: (state) => {
        // 	state.placeStockReceiptItemDialog = initialState.placeStockReceiptItemDialog;
        // },
        //
        // Invoice Upload
        //
        openUploadInvoiceDialog: (state) => {
            state.uploadInvoiceDialog.open = true;
        },
        closeUploadInvoiceDialog: (state) => {
            state.uploadInvoiceDialog = initialState.uploadInvoiceDialog;
        },
        setInvoiceUploadStep: (state, action) => {
            state.uploadInvoiceDialog.activeStep = action.payload;
        },
        setIsInvoiceUploading: (state, action) => {
            state.uploadInvoiceDialog.isInvoiceUploading = action.payload;
        },
        setInvoiceSettings: (state, action) => {
            state.uploadInvoiceDialog.invoiceSettings = action.payload ?? userEmptyInvoiceSettings;
        },
        setInvoiceSettingsArticlePosition: (state, action) => {
            // `articleColumn.column` field must already been set, and `articleColumn` is already not null.
            if (!state.uploadInvoiceDialog.invoiceSettings.articleColumn)
                return;
            state.uploadInvoiceDialog.invoiceSettings.articleColumn.position = action.payload;
        },
        setInvoice: (state, action) => {
            state.uploadInvoiceDialog.invoice = action.payload.invoice;
            state.uploadInvoiceDialog.invoiceUploadErrorMessage = action.payload.uploadErrorMessage;
        },
        setInvoiceActiveSetting: (state, action) => {
            state.uploadInvoiceDialog.activeSettingKey = action.payload;
        },
        bindInvoiceSetting: (state, action) => {
            const { activeSettingKey, invoiceSettings } = state.uploadInvoiceDialog;
            if (!invoiceSettings)
                return;
            if (!state.uploadInvoiceDialog.invoiceSettings)
                return;
            switch (activeSettingKey) {
                case 'invoiceIdCell':
                case 'dateCell':
                    state.uploadInvoiceDialog.invoiceSettings[activeSettingKey] = action.payload;
                    break;
                case 'articleColumn':
                    state.uploadInvoiceDialog.invoiceSettings.articleColumn = {
                        ...(invoiceSettings.articleColumn ?? { position: DetailArticlePosition.WholeString }),
                        column: action.payload.column,
                    };
                    break;
                case 'startRow':
                    state.uploadInvoiceDialog.invoiceSettings.startRow = action.payload.row;
                    break;
                default:
                    state.uploadInvoiceDialog.invoiceSettings[activeSettingKey] = action.payload.column;
                    break;
            }
        },
        //
        // Surplus items
        //
        openUploadSurplusItemsDialog: (state) => {
            state.uploadSurplusItemsDialog.open = true;
        },
        closeUploadSurplusItemsDialog: (state) => {
            state.uploadSurplusItemsDialog = initialState.uploadSurplusItemsDialog;
        },
        setSurplusItemsUploadStep: (state, action) => {
            state.uploadSurplusItemsDialog.activeStep = action.payload;
        },
        setIsSurplusItemsRequestFetching: (state, action) => {
            state.uploadSurplusItemsDialog.isRequestFetching = action.payload;
        },
        setSurplusItemsPreview: (state, action) => {
            state.uploadSurplusItemsDialog.surplusItemsPreview = action.payload.surplusItemsPreview;
            state.uploadSurplusItemsDialog.itemsUploadErrorMessage = action.payload.uploadErrorMessage;
        },
        setSurplusItemsActiveSetting: (state, action) => {
            state.uploadSurplusItemsDialog.activeSettingKey = action.payload;
        },
        bindSurplusItemsSetting: (state, action) => {
            const { activeSettingKey } = state.uploadSurplusItemsDialog;
            switch (activeSettingKey) {
                case 'startRow':
                    state.uploadSurplusItemsDialog.surplusItemsSettings.startRow = action.payload.rowIndex;
                    break;
                default:
                    state.uploadSurplusItemsDialog.surplusItemsSettings[activeSettingKey] = action.payload.columnIndex;
                    break;
            }
            state.uploadSurplusItemsDialog.isEverySettingDefined = isEveryFieldDefined(state.uploadSurplusItemsDialog.surplusItemsSettings);
        },
        setSurplusItemsSearchResult: (state, action) => {
            state.uploadSurplusItemsDialog.surplusItemsPrices = action.payload.surplusItemsPrices;
            state.uploadSurplusItemsDialog.searchErrorMessage = action.payload.errorMessage;
        },
    },
});
const selectInvoiceSettings = (state) => state.stockReceipt.uploadInvoiceDialog.invoiceSettings;
const selectInvoiceActiveSettingKey = (state) => state.stockReceipt.uploadInvoiceDialog.activeSettingKey;
export const selectInvoiceActiveSettingValue = createSelector(selectInvoiceSettings, selectInvoiceActiveSettingKey, (settings, activeSettingKey) => {
    return settings?.[activeSettingKey];
});
export const selectAllDefinedInvoiceSettings = createSelector(selectInvoiceSettings, (settings) => {
    return isEveryFieldDefined(settings);
});
const selectSurplusItemsSettings = (state) => state.stockReceipt.uploadSurplusItemsDialog.surplusItemsSettings;
const selectSurplusItemsActiveSettingKey = (state) => state.stockReceipt.uploadSurplusItemsDialog.activeSettingKey;
export const selectSurplusItemsActiveSettingValue = createSelector(selectSurplusItemsSettings, selectSurplusItemsActiveSettingKey, (settings, activeSettingKey) => {
    return settings?.[activeSettingKey];
});
export const selectAllDefinedSurplusItemsSettings = createSelector(selectSurplusItemsSettings, (settings) => {
    return isEveryFieldDefined(settings);
});
export const { actions: stockReceiptActions, reducer: stockReceiptStateReducer } = slice;
